import * as React from "react";
import { Link } from "react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ColumnDef } from "@tanstack/react-table";
import { BellRing } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NativeSelect } from "@/components/ui/input";
import { ActionDialog } from "@/components/action-dialog";
import { DataTable } from "@/components/data-table";
import { PageHeader } from "@/components/common";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { ago, dateTime, money, shortId } from "@/lib/format";
import { label } from "@/lib/labels";

interface Row {
  invoiceId: string;
  taskId: string;
  service: string;
  providerId: string;
  provider: string;
  receiptNumber: string | null;
  providerPayout: { amountMinor: string; currency: string } | null;
  confirmedAt: string | null;
}

function Remind({ row }: { row: Row }) {
  const qc = useQueryClient();
  return (
    <ActionDialog
      trigger={
        <Button variant="ghost" size="sm">
          <BellRing /> Hatırlat
        </Button>
      }
      title="Hizmet verene hatırlat"
      description={`${row.provider} fişini/faturasını yüklemesi için uygulama içi ve e-posta bildirimi alır (günde en çok bir).`}
      confirmLabel="Hatırlatma gönder"
      success="Hatırlatma gönderildi."
      onSubmit={async (key) => {
        await api(`/v1/admin/tasks/${row.taskId}/receipt-reminder`, {
          method: "POST",
          idempotencyKey: key,
          body: {},
        });
        await qc.invalidateQueries({ queryKey: ["admin-missing-receipts"] });
      }}
    />
  );
}

export function MissingReceiptsPage() {
  const { can } = useAuth();
  const [days, setDays] = React.useState("7");
  const q = useQuery({
    queryKey: ["admin-missing-receipts", days],
    queryFn: () => api<{ items: Row[] }>(`/v1/admin/tasks/missing-receipts?olderThanDays=${days}`),
  });
  const columns: ColumnDef<Row, unknown>[] = [
    {
      id: "task",
      header: "İş",
      cell: ({ row }) => (
        <Link className="text-primary hover:underline" to={`/tasks/${row.original.taskId}`}>
          {label(row.original.service)}{" "}
          <span className="font-mono text-xs text-muted-foreground">
            {shortId(row.original.taskId)}
          </span>
        </Link>
      ),
    },
    {
      id: "provider",
      header: "Hizmet veren",
      cell: ({ row }) => (
        <Link className="hover:underline" to={`/providers/${row.original.providerId}`}>
          {row.original.provider}
        </Link>
      ),
    },
    {
      id: "payout",
      header: "Hizmet verene",
      cell: ({ row }) => money(row.original.providerPayout),
    },
    {
      id: "receipt",
      header: "Poji makbuzu",
      cell: ({ row }) => (
        <span className="font-mono text-xs">{row.original.receiptNumber ?? "—"}</span>
      ),
    },
    {
      id: "confirmed",
      header: "Müşteri onayı",
      cell: ({ row }) => (
        <span title={dateTime(row.original.confirmedAt)}>{ago(row.original.confirmedAt)}</span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) =>
        can("tasks.manage") ? (
          <div className="flex justify-end">
            <Remind row={row.original} />
          </div>
        ) : null,
    },
  ];
  return (
    <>
      <PageHeader
        title="Eksik fiş"
        description="Müşteri onaylamış, hizmet veren kendi fişini/faturasını yüklememiş işler (K-114). Uygulama 1, 3 ve 7. gün kendisi hatırlatır; ödeme bekletilmez. Vergi yükümlülüğü hizmet verende (K-042)."
        actions={
          <NativeSelect aria-label="Süre" value={days} onChange={(e) => setDays(e.target.value)}>
            <option value="0">Tümü</option>
            <option value="7">7 günden eski</option>
            <option value="30">30 günden eski</option>
          </NativeSelect>
        }
      />
      <DataTable
        columns={columns}
        data={q.data?.items}
        isLoading={q.isPending}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty="Eksik fiş yok."
      />
    </>
  );
}
